<script setup>
import { computed, onMounted, reactive, ref, shallowRef } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import { fmt, transactionCount } from '../../stores'
import { state, closeSheet, openSheet, toast } from '../../stores/state'
import { count } from '../../stores/usage'
import { canSuggest, suggestionsFor, suggestionsReady, wantSuggestions } from '../../stores/suggest'
import { allTransactions, transactionsByIds } from '../../stores/transactions'
import { errorText, t } from '../../stores/i18n'
import { setSetting } from '../../stores/settings'
import { importTransactions, settleTransactions } from '../../stores/import'
import { deleteTransactions } from '../../stores/bulk'
import { readWorkbook } from '../../lib/workbook'
import { markDuplicates, stalePending, summarise } from '../../lib/statement'
import { importerFor, matchRuleFor } from '../../lib/importers'
import { dayLabel } from '../../lib/dates'

const FIELDS = [
  { key: 'date' },
  { key: 'description' },
  { key: 'amount' },
  { key: 'type' },
  { key: 'credit' },
  { key: 'debit' },
]

const fileName = ref('')
const book = ref(null)
const sheetIndex = ref(0)
const busy = ref('')
const error = ref('')
const dragging = ref(false)
const input = ref(null)
const override = reactive({ dateOrder: null, columns: null })
const adoptCurrency = ref(false)

const sheet = computed(() => book.value?.sheets[sheetIndex.value] ?? null)

/**
 * Which bank wrote this file. It is a setting rather than a guess: reading a
 * statement with the wrong bank's rules mis-reads it quietly, and that is not
 * worth risking to save one dropdown in Settings.
 */
const importer = computed(() => importerFor(state.settings.bank))

const parsed = computed(() => {
  if (!sheet.value) return null
  try {
    return importer.value.read(sheet.value.rows, {
      dateOrder: override.dateOrder ?? undefined,
      columns: override.columns ?? undefined,
    })
  } catch {
    return null
  }
})

/**
 * The ledger, read when the sheet opens: deciding what is already imported is
 * one of the few questions that has to look at every row (Intesa's rule even
 * learns the account holder's name from them). Read for this and let go of
 * with the sheet.
 */
const existing = shallowRef([])
onMounted(async () => {
  wantSuggestions()
  existing.value = await allTransactions()
})

// Each bank decides what "already imported" means for its own statements, and
// is handed the ledger to decide it with.
const entries = computed(() =>
  parsed.value
    ? markDuplicates(
        parsed.value.entries,
        existing.value,
        matchRuleFor(importer.value, existing.value),
      )
    : [],
)
/** Rows the same as one already here, but dated differently — settled since. */
const moved = computed(() => entries.value.filter((e) => e.movedBy).length)

/** What this bank calls a row the statement has not finished with. */
const rule = computed(() => matchRuleFor(importer.value, existing.value))
const isUnsettled = (entry) => !!rule.value.unsettled?.(entry)

/**
 * Rows already here that this statement has since settled: the stored row is
 * holding the authorisation, and this entry is holding the payment. A stored
 * row the bank's rule calls pending counts too, though it was saved as settled:
 * it came in before the rule knew that kind of row could wait (ATM withdrawals).
 */
const settling = computed(() =>
  entries.value
    .filter((e) => e.duplicate && e.matched && (!e.matched.settled || isUnsettled(e.matched)) && !isUnsettled(e))
    .map((e) => ({
      id: e.matched.id,
      date: e.date,
      description: e.description,
      payment_type: e.type ?? '',
    })),
)
/**
 * Pending rows already here that this statement no longer lists at all: the
 * bank dropped the authorisation, or settled it at an amount that made it
 * another row. Only inside the statement's own days — a pending row newer
 * than the statement, or older, is not something it can speak for.
 */
const stale = computed(() => stalePending(existing.value, entries.value))

/**
 * Clearing what is pending: the rows this statement settled are brought up to
 * date in place (same row, so their category and tags stay), and the ones it
 * no longer lists are removed. Offered, and ticked — a new statement is the
 * bank's latest word on what is still pending.
 */
const clearPending = ref(true)
const pendingWork = computed(() => settling.value.length + stale.value.length)

/** How many of the rows coming in are ones the bank has not finished with. */
const pending = computed(() => fresh.value.filter(isUnsettled).length)

/** Whether this entry is the settled half of a row already in the ledger. */
const settles = (entry) => !!entry.duplicate && !!entry.matched && !entry.matched.settled
const fresh = computed(() => entries.value.filter((e) => !e.duplicate))
const duplicates = computed(() => entries.value.length - fresh.value.length)
const stats = computed(() => summarise(fresh.value))

const columnLetter = (i) =>
  (i >= 26 ? String.fromCharCode(64 + Math.floor(i / 26)) : '') + String.fromCharCode(65 + (i % 26))

const columnOptions = computed(() => {
  const rows = sheet.value?.rows ?? []
  const width = rows.reduce((w, row) => Math.max(w, row.length), 0)
  const header = parsed.value?.headerRow >= 0 ? rows[parsed.value.headerRow] : []
  return Array.from({ length: width }, (_, i) => ({
    value: i,
    label: String(header?.[i] ?? '').trim().slice(0, 24) || `Column ${columnLetter(i)}`,
  }))
})

const otherCurrency = computed(() => {
  const found = parsed.value?.currency
  return found && found !== state.settings.currency ? found : null
})

function reset() {
  book.value = null
  sheetIndex.value = 0
  override.dateOrder = null
  override.columns = null
  error.value = ''
  fileName.value = ''
}

async function take(file) {
  if (!file) return
  reset()
  busy.value = 'read'
  fileName.value = file.name
  try {
    const workbook = await readWorkbook(await file.arrayBuffer())
    // Default to the sheet with the most in it — extras are usually notes.
    const biggest = workbook.sheets.reduce((best, s, i) => (s.rows.length > workbook.sheets[best].rows.length ? i : best), 0)
    book.value = workbook
    sheetIndex.value = biggest
    adoptCurrency.value = transactionCount.value === 0
  } catch (err) {
    error.value = errorText(err)
    book.value = null
  } finally {
    busy.value = ''
  }
}

function pickSheet(index) {
  sheetIndex.value = Number(index)
  override.dateOrder = null
  override.columns = null
}

function setColumn(key, value) {
  const columns = { ...(override.columns ?? parsed.value.columns), [key]: value === '' ? null : Number(value) }
  // One signed amount column or a debit/credit pair — picking one drops the other.
  if (key === 'amount' && columns.amount != null) Object.assign(columns, { credit: null, debit: null })
  if ((key === 'credit' || key === 'debit') && columns[key] != null) columns.amount = null
  override.columns = columns
}

function onDrop(event) {
  dragging.value = false
  take(event.dataTransfer?.files?.[0])
}

async function run() {
  if (!fresh.value.length && !(clearPending.value && pendingWork.value)) return
  busy.value = 'import'
  error.value = ''
  try {
    const ids = await importTransactions(
      fresh.value.map((entry) => ({ ...entry, unsettled: isUnsettled(entry) })),
    )
    if (clearPending.value && settling.value.length) await settleTransactions(settling.value)
    if (clearPending.value && stale.value.length) await deleteTransactions(stale.value.map((tx) => tx.id))
    if (otherCurrency.value && adoptCurrency.value) await setSetting('currency', otherCurrency.value)
    count('import', importer.value?.id ?? '')
    if (ids.length) count('transaction.add', 'import')
    toast(t('import.imported', { count: ids.length }), 'good')
    // The rows are in. If the ledger has an opinion about what they are, that
    // is a separate question with its own yes and no.
    await suggestionsReady()
    const written = canSuggest.value ? await transactionsByIds(ids) : []
    const worthReviewing = written.some((tx) => {
      const guess = suggestionsFor(tx)
      return guess.category || guess.tags.length
    })
    // The rows go along: they were just read, and the review opens full.
    if (worthReviewing) openSheet('suggest', { ids, rows: written })
    else closeSheet()
  } catch (err) {
    error.value = t('import.failed', { reason: errorText(err) })
  } finally {
    busy.value = ''
  }
}
</script>

<template>
  <Sheet
    wide
    :title="t('import.title')"
    :subtitle="t('import.subtitle')"
    @close="closeSheet"
  >
    <div class="grid gap-5">
      <div
        v-if="!book"
        class="rounded-2xl border border-dashed p-8 text-center transition"
        :class="dragging ? 'border-accent bg-accent/8' : 'border-line'"
        @dragover.prevent="dragging = true"
        @dragleave="dragging = false"
        @drop.prevent="onDrop"
      >
        <Icon name="upload" :size="24" class="mx-auto text-muted" />
        <p class="mt-3 text-[14px] font-medium">{{ t('import.drop') }}</p>
        <p class="mt-1 text-[12.5px] text-faint">{{ t('import.or') }}</p>
        <button class="btn-soft mt-3" :disabled="busy === 'read'" @click="input.click()">
          {{ busy === 'read' ? t('import.reading') : t('import.chooseFile') }}
        </button>
        <input
          ref="input"
          type="file"
          accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          class="hidden"
          @change="take($event.target.files?.[0]); $event.target.value = ''"
        />
      </div>

      <template v-else>
        <div class="flex items-center gap-3">
          <Icon name="wallet" :size="18" class="shrink-0 text-muted" />
          <p class="min-w-0 flex-1 truncate text-[13.5px] font-medium">{{ fileName }}</p>
          <button class="btn-ghost !px-2 !py-1 text-[12.5px]" @click="reset">{{ t('import.chooseAnother') }}</button>
        </div>

        <div v-if="book.sheets.length > 1">
          <p class="label">{{ t('import.bank') }}</p>
          <p class="input !py-2.5 text-muted">{{ t(`importer.${importer.id}`) }}</p>
          <p class="mt-1.5 text-[12px] text-faint">{{ t('import.bankInSettings') }}</p>
        </div>

        <div>
          <label class="label" for="im-sheet">{{ t('import.sheet') }}</label>
          <select id="im-sheet" class="input" :value="sheetIndex" @change="pickSheet($event.target.value)">
            <option v-for="(s, i) in book.sheets" :key="i" :value="i">
              {{ t('import.sheetRows', { name: s.name, count: s.rows.length }) }}
            </option>
          </select>
        </div>

        <div>
          <p class="mb-2 text-[12.5px] font-semibold text-muted">{{ t('import.columns') }}</p>
          <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <div v-for="field in FIELDS" :key="field.key">
              <label class="label" :for="`im-${field.key}`">{{ t(`import.field.${field.key}`) }}</label>
              <select
                :id="`im-${field.key}`"
                class="input !py-2 !text-[13px]"
                :value="parsed?.columns?.[field.key] ?? ''"
                @change="setColumn(field.key, $event.target.value)"
              >
                <option value="">—</option>
                <option v-for="option in columnOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
          <div class="mt-3 flex flex-wrap items-center gap-2">
            <span class="text-[12.5px] text-faint">{{ t('import.datesReadAs') }}</span>
            <button
              v-for="option in [{ v: 'dmy', l: t('import.dayFirst') }, { v: 'mdy', l: t('import.monthFirst') }]"
              :key="option.v"
              class="chip !py-1 !text-[12.5px]"
              :class="parsed?.dateOrder === option.v && 'chip-on'"
              @click="override.dateOrder = option.v"
            >
              {{ option.l }}
            </button>
          </div>
        </div>

        <div v-if="entries.length" class="rounded-xl border border-line-soft bg-raised/50 p-3.5">
          <dl class="tnum grid grid-cols-2 gap-y-1.5 text-[13px]">
            <dt class="text-muted">{{ t('import.ready') }}</dt>
            <dd class="text-right font-semibold">{{ stats.count }}</dd>
            <dt class="text-muted">{{ t('import.period') }}</dt>
            <dd class="text-right">
              {{ stats.from ? `${dayLabel(stats.from)} — ${dayLabel(stats.to)}` : '—' }}
            </dd>
            <dt class="text-muted">{{ t('import.field.credit') }}</dt>
            <dd class="text-right text-accent">{{ fmt(stats.income) }}</dd>
            <dt class="text-muted">{{ t('import.field.debit') }}</dt>
            <dd class="text-right">{{ fmt(stats.spent) }}</dd>
            <template v-if="moved">
              <dt class="text-muted">{{ t('import.settled') }}</dt>
              <dd class="text-right">{{ t('import.settledCount', { count: moved }) }}</dd>
            </template>
            <template v-if="pending">
              <dt class="text-muted">{{ t('import.pending') }}</dt>
              <dd class="text-right text-warn">{{ pending }}</dd>
            </template>
            <template v-if="duplicates">
              <dt class="text-muted">{{ t('import.alreadyHere') }}</dt>
              <dd class="text-right">{{ t('import.skippedN', { count: duplicates }) }}</dd>
            </template>
            <template v-if="parsed?.skipped">
              <dt class="text-muted">{{ t('import.unreadableRows') }}</dt>
              <dd class="text-right">{{ t('import.ignoredN', { count: parsed.skipped }) }}</dd>
            </template>
          </dl>
        </div>

        <p v-else class="rounded-xl border border-line-soft bg-raised/50 p-3.5 text-[13px] text-muted">
          {{ t('import.nothingReadable') }}
        </p>

        <!-- Offered, and ticked, but not assumed: it rewrites rows already in the
             ledger and removes others, which is not something to do quietly. -->
        <label
          v-if="pendingWork"
          class="flex items-start gap-3 rounded-xl border border-line-soft bg-raised/50 p-3.5"
          data-clear-pending
        >
          <input v-model="clearPending" type="checkbox" class="mt-0.5 size-4 accent-[var(--c-accent)]" />
          <span>
            <span class="block text-[13.5px] font-medium">{{ t('import.clearPending') }}</span>
            <span v-if="settling.length" class="mt-0.5 block text-[12.5px] text-muted">
              {{ t('import.pendingSettled', { count: settling.length }) }}
            </span>
            <span v-if="stale.length" class="mt-0.5 block text-[12.5px] text-muted">
              {{ t('import.pendingGone', { count: stale.length }) }}
            </span>
          </span>
        </label>

        <label v-if="otherCurrency" class="flex items-start gap-3 rounded-xl border border-line-soft bg-raised/50 p-3.5">
          <input v-model="adoptCurrency" type="checkbox" class="mt-0.5 size-4 accent-[var(--c-accent)]" />
          <span>
            <span class="block text-[13.5px] font-medium">{{ t('import.otherCurrency', { currency: otherCurrency }) }}</span>
            <span class="mt-0.5 block text-[12.5px] text-muted">
              {{
                t('import.otherCurrencyDetail', {
                  current: state.settings.currency,
                  other: otherCurrency,
                })
              }}
            </span>
          </span>
        </label>

        <div v-if="entries.length" class="overflow-x-auto">
          <table class="w-full text-left text-[12.5px]">
            <thead class="text-faint">
              <tr>
                <th class="py-1.5 pr-3 font-medium">{{ t('import.field.date') }}</th>
                <th class="py-1.5 pr-3 font-medium">{{ t('import.field.description') }}</th>
                <th class="py-1.5 text-right font-medium">{{ t('import.field.amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in entries.slice(0, 8)" :key="entry.row" class="border-t border-line-soft">
                <td class="py-1.5 pr-3 whitespace-nowrap tnum" :class="entry.duplicate && 'text-faint'">
                  {{ entry.date }}
                </td>
                <td class="max-w-[22ch] truncate py-1.5 pr-3 sm:max-w-[40ch]" :class="entry.duplicate ? 'text-faint' : 'text-muted'">
                  {{ entry.description }}
                  <!-- Three things a row can be, and they are not the same
                       thing: one the bank is still holding, one that settles a
                       row already here, and one that is simply already here. -->
                  <span v-if="isUnsettled(entry)" class="tag bg-warn-soft text-warn">
                    {{ t('import.pendingTag') }}
                  </span>
                  <span v-else-if="settles(entry)" class="tag bg-accent-soft text-accent">
                    {{ t('import.settlesTag') }}
                  </span>
                  <span v-else-if="entry.duplicate" class="text-faint">
                    · {{ t('import.alreadyHereTag') }}
                  </span>
                </td>
                <td class="tnum py-1.5 text-right" :class="entry.amount > 0 ? 'text-accent' : ''">
                  {{ fmt(entry.amount, { signed: true }) }}
                </td>
              </tr>
            </tbody>
          </table>
          <p v-if="entries.length > 8" class="mt-2 text-[12px] text-faint">
            {{ t('import.andMore', { count: entries.length - 8 }) }}
          </p>
        </div>

      </template>

      <p v-if="error" class="rounded-xl bg-danger/12 px-3.5 py-3 text-[13px] text-danger">{{ error }}</p>
    </div>

    <template #footer>
      <p class="flex-1 text-[12.5px] text-faint">
        {{ t('import.footnote') }}
      </p>
      <button class="btn-ghost" @click="closeSheet">{{ t('common.cancel') }}</button>
      <button
        class="btn-primary"
        data-primary
        :aria-keyshortcuts="ariaCombo('mod+enter')"
        :disabled="(!fresh.length && !(clearPending && pendingWork)) || busy === 'import'"
        @click="run"
      >
        {{
          busy === 'import'
            ? t('import.importing')
            : fresh.length
              ? t('import.importN', { count: fresh.length })
              : t('import.updatePending')
        }}
        <Kbd combo="mod+enter" />
      </button>
    </template>
  </Sheet>
</template>
